/**
 * Backend Analytics Provider
 * Sends system events to backend Elasticsearch
 * Only handles events it cares about - no need to know about other providers
 */

import type { AnalyticsProvider } from './base.provider'
import { AnalyticsEvents } from '../types/events'
import { backendAnalyticsService } from '../services/backend-analytics.service'

// Backend event name mapping (ONLY events this provider handles)
const BACKEND_EVENT_MAP: Record<string, string> = {
  [AnalyticsEvents.USER_AUTO_LOGOUT]: 'client-portal-user-auto-logout',
  [AnalyticsEvents.COMMUNITIES_USER_AUTO_LOGOUT]: 'communities-user-auto-logout',
  [AnalyticsEvents.GENERATE_TOKEN_FAILED]: 'generate-token-failed',
  [AnalyticsEvents.GENERATE_TOKEN_SUCCESS]: 'generate-token-success',
  [AnalyticsEvents.COMMUNITIES_GROUP_ACCESS]: 'communities-group-access-event',
  [AnalyticsEvents.NO_COMMUNITIES_FOUND]: 'no-communities-found',
  [AnalyticsEvents.COURSE_ACCESS_REFRESH]: 'course-access-refresh',
  [AnalyticsEvents.PUSH_NOTIFICATION_INITIALIZED]: 'client-portal-initialize-push-event',
  [AnalyticsEvents.FORCE_UPDATE_EVENT]: 'client-portal-force-app-update-event',
}

export class BackendAnalyticsProvider implements AnalyticsProvider {
  name = 'BackendAnalytics'
  private debug = false
  private currentUser: {
    userId?: string
    locationId?: string
    contactId?: string
  } = {}

  constructor(debug = false) {
    this.debug = debug
  }

  track(event: string, properties?: Record<string, any>): void {
    // Only handle events we have mapping for
    const backendEventName = BACKEND_EVENT_MAP[event]
    if (!backendEventName) {
      return // Not our event, skip silently
    }

    try {
      // Build payload
      const payload = {
        eventName: backendEventName,
        locationId: properties?.locationId || this.currentUser.locationId || 'NA',
        contactId: properties?.contactId || this.currentUser.contactId || 'NA',
        groupId: properties?.groupId || 'NA',
        eventData: {
          ...properties,
          userId: this.currentUser.userId,
          platform: properties?.platform || 'web',
        },
      }

      // Send via service (fire and forget)
      backendAnalyticsService.trackEvent(payload)

      if (this.debug) {
        console.log('[BackendAnalytics] Event tracked:', backendEventName, payload)
      }
    } catch (error) {
      console.error('[BackendAnalytics] Error:', error)
    }
  }

  identify(userId: string, traits?: Record<string, any>): void {
    // Store user context for enriching events
    this.currentUser = {
      userId,
      contactId: traits?.contactId || userId,
      locationId: traits?.locationId,
    }

    if (this.debug) {
      console.log('[BackendAnalytics] User identified:', this.currentUser)
    }
  }
}



